/**
 * 文件路径: D:/XXY/MeiShan_Project/frontend/shared/api/enterprise_merchant_services.ts
 * 企业×商户餐厅服务关系 API（按“企业职场 × 商户餐厅”维度）
 */

import { httpClient } from './http'

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function toRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {}
}

// ⚠️ 注意：后端使用常量 code（如 'ALWS01'），不是英文别名。
// 约定：所有入参/出参中的消费类型一律使用 code（string）。
export type ConsumptionTypeCode = string  // 后端常量 code（如 'ALWS01', 'BLWM01', 'CLGR01'）

export interface EmsRelation {
  /**
   * 输入：后端 EMS ReadDTO（EnterpriseMerchantService）
   * 输出：用于前端渲染/编辑的 EMS 数据
   */
  id: number
  workplace: number
  restaurant: number
  is_active: boolean
  consumption_type: ConsumptionTypeCode
  consumption_type_name?: string | null
  // ManyToMany：默认返回餐段主键数组；同时提供 detail 便于直接展示（code+label）
  meal_segments?: number[]
  meal_segments_detail?: Array<{ code: string; label: string }>
  // 运营端配置字段
  commission_rate?: number | null
  meal_budget?: number | null
  max_daily_orders?: number | null
  max_radius_km?: number | null
  sort_order?: number | null
  holiday_service?: boolean | null
  reservation_enabled?: boolean | null
  order_deadline_config?: Record<string, unknown> | null
  payment_qr_url?: string | null
  created_at?: string
  // ⭐ P1-D2: 到店消费时间规则覆盖（按餐段 code 配置）
  dine_in_time_rules?: Record<string, { start_time?: string; end_time?: string; allowed_days?: number[] }> | null
  // 展示字段
  workplace_detail?: Record<string, unknown> | null
  restaurant_detail?: Record<string, unknown> | null
}

export interface EmsQuery {
  enterprise?: string // 兼容旧字段
  enterprise_id?: number | string
  workplace_id?: number | string
  restaurant_id?: number | string
  consumption_type?: ConsumptionTypeCode  // ✅ P4-FIX: 使用后端常量 code
  is_active?: boolean
  segment_code?: string
  page?: number
  page_size?: number
}

class EnterpriseMerchantServicesApiService {
  // 后端实际路由为 /api/v1/service-relations/（见 config/api_router.py）
  // ⚠️ 必须使用相对路径（不要以 '/' 开头），避免绕过 httpClient.baseURL=/api/v1/
  private readonly base = 'service-relations/'

  async list(params?: EmsQuery): Promise<EmsRelation[]> {
    // 策略A：强约束必填参数（避免漏传导致后端返回全量/重复）
    if (!params?.consumption_type || typeof params.is_active !== 'boolean') {
      throw new Error('EMS 查询缺少必填参数：consumption_type 与 is_active')
    }
    // 输入：params（过滤条件）；输出：EmsRelation 数组（兼容分页结构自动解包 results）
    const payload: unknown = await httpClient.get<unknown>(this.base, { params })
    return this.unwrapList(payload)
  }

  async listAll(params?: EmsQuery): Promise<EmsRelation[]> {
    // 输入：params（过滤条件，可含 page_size）；输出：尽可能拉全的 EmsRelation[]（自动翻页直到 next 为空）
    //
    // ⚠️ 关键点：
    // - 后端可能强制 page_size 上限（例如固定 20），因此不能用 “batch.length < pageSize” 判定结束；
    // - 必须以分页响应的 next 字段作为是否继续的依据，避免提前 break 漏数据。
    if (!params?.consumption_type || typeof params.is_active !== 'boolean') {
      throw new Error('EMS 全量查询缺少必填参数：consumption_type 与 is_active')
    }

    const requested = typeof params?.page_size === 'number' ? params.page_size : 100
    const pageSize = Math.max(1, Math.min(requested, 100))
    const axios = httpClient.getAxiosInstance()

    const out: EmsRelation[] = []
    let page = params?.page ?? 1
    let hasMore = true

    for (let i = 0; i < 200 && hasMore; i += 1) {
      const resp = await axios.get(this.base, { params: { ...params, page, page_size: pageSize } })
      const pageData = (resp.data?.data || resp.data || {}) as Record<string, unknown>
      const nextUrl = String(pageData.next || '')
      const batch = this.unwrapList(pageData)
      if (batch.length === 0) break
      out.push(...batch)
      hasMore = Boolean(nextUrl)
      page += 1
    }

    return out
  }

  private unwrapList(payload: unknown): EmsRelation[] {
    if (Array.isArray(payload)) return payload as EmsRelation[]
    // 兼容：DRF 分页 / 项目分页口径
    if (isRecord(payload) && Array.isArray(payload.results)) return payload.results as EmsRelation[]
    if (isRecord(payload) && isRecord(payload.data) && Array.isArray(payload.data.results)) return payload.data.results as EmsRelation[]
    return []
  }

  async create(payload: Partial<EmsRelation> & { workplace: number; restaurant: number; consumption_type: string }): Promise<EmsRelation> {
    // 输入：payload（workplace/restaurant/consumption_type 等）；输出：创建后的 EMS
    return await httpClient.post<EmsRelation>(this.base, payload)
  }

  async patch(id: number | string, payload: Record<string, unknown>): Promise<EmsRelation> {
    // 输入：id + patch payload；输出：更新后的 EMS
    return await httpClient.patch<EmsRelation>(`${this.base}${id}/`, payload)
  }

  async destroy(id: number | string): Promise<void> {
    // 输入：id；输出：无（204）
    await httpClient.delete(`${this.base}${id}/`)
  }

  async generateQrCode(id: number | string): Promise<{ payment_qr_url?: string } | null> {
    // 输入：EMS id；输出：{payment_qr_url}（包装或非包装均兼容）
    const payload: unknown = await httpClient.post<unknown>(`${this.base}${id}/generate-qr-code/`, {})
    const p = toRecord(payload)
    const data = toRecord(p.data)
    if (typeof data.payment_qr_url === 'string') return { payment_qr_url: data.payment_qr_url }
    if (typeof p.payment_qr_url === 'string') return { payment_qr_url: p.payment_qr_url }
    return null
  }

  // ⭐ P3-4: 职场分餐点 QR 码（MEAL_POINT）

  /**
   * 获取单个 EMS 的职场分餐点二维码（仅 CLGR01）
   * GET /api/v1/service-relations/{ems_id}/meal-point-qrcode/
   * 确定性生成（CRC32 校验，同参数同结果，永久有效）
   */
  async getMealPointQrCode(id: number | string): Promise<MealPointQrCodeData | null> {
    try {
      const payload: unknown = await httpClient.get<unknown>(`${this.base}${id}/meal-point-qrcode/`)
      const p = toRecord(payload)
      // ⭐ 兼容双层结构：httpClient.extractPayload() 可能已解包 data，也可能未解包
      // - 已解包：p = { ems_id, qrcode_data, ... }（p.data 不存在）
      // - 未解包：p = { status, data: { ems_id, qrcode_data, ... } }
      const inner = isRecord(p.data) ? toRecord(p.data) : p
      if (typeof inner.qrcode_data === 'string') {
        return {
          ems_id: String(inner.ems_id ?? id),
          qrcode_data: inner.qrcode_data,
          workplace_id: String(inner.workplace_id ?? ''),
          workplace_name: String(inner.workplace_name ?? ''),
          restaurant_id: String(inner.restaurant_id ?? ''),
          restaurant_name: String(inner.restaurant_name ?? ''),
          merchant_name: String(inner.merchant_name ?? ''),
          operator_id: String(inner.operator_id ?? ''),
          version: String(inner.version ?? 'v1'),
          is_permanent: Boolean(inner.is_permanent ?? true),
        }
      }
      return null
    } catch (error: unknown) {
      // ⭐ 不再静默吞掉错误：后端 400（非 CLGR01）等应传递给调用方显示
      const rec = (error && typeof error === 'object') ? (error as Record<string, unknown>) : {}
      const respData = rec.response && typeof rec.response === 'object'
        ? (rec.response as Record<string, unknown>).data
        : undefined
      const backendMsg = respData && typeof respData === 'object'
        ? String((respData as Record<string, unknown>).message || '')
        : ''
      const msg = backendMsg || '获取分餐点码失败'
      throw new Error(msg)
    }
  }

  /**
   * 批量获取职场分餐点二维码
   * GET /api/v1/service-relations/batch-meal-point-qrcodes/?workplace_id=X 或 enterprise_id=Y
   * 用于运营端批量打印分餐点二维码
   */
  async batchMealPointQrCodes(params: { workplace_id?: number | string; enterprise_id?: number | string }): Promise<MealPointQrCodeData[]> {
    try {
      const payload: unknown = await httpClient.get<unknown>(`${this.base}batch-meal-point-qrcodes/`, { params })
      // ⭐ 兼容双层结构：extractPayload 可能已解包
      // - 已解包：payload 直接是数组 [...]
      // - 未解包：payload = { status, data: [...] }
      const arr = Array.isArray(payload)
        ? payload
        : (isRecord(payload) && Array.isArray((payload as Record<string, unknown>).data))
          ? (payload as Record<string, unknown>).data as unknown[]
          : []
      return arr
        .map((item: unknown) => {
          const d = toRecord(item)
          if (typeof d.qrcode_data !== 'string') return null
          return {
            ems_id: String(d.ems_id ?? ''),
            qrcode_data: d.qrcode_data,
            workplace_id: String(d.workplace_id ?? ''),
            workplace_name: String(d.workplace_name ?? ''),
            restaurant_id: String(d.restaurant_id ?? ''),
            restaurant_name: String(d.restaurant_name ?? ''),
            merchant_name: String(d.merchant_name ?? ''),
            operator_id: String(d.operator_id ?? ''),
            version: String(d.version ?? 'v1'),
            is_permanent: Boolean(d.is_permanent ?? true),
          } as MealPointQrCodeData
        })
        .filter((x): x is MealPointQrCodeData => x !== null)
    } catch {
      return []
    }
  }
}

/** 职场分餐点 QR 码数据 */
export interface MealPointQrCodeData {
  ems_id: string
  qrcode_data: string
  workplace_id: string
  workplace_name: string
  restaurant_id: string
  restaurant_name: string
  merchant_name: string
  operator_id: string
  version: string
  is_permanent: boolean
}

export const emsApi = new EnterpriseMerchantServicesApiService()


